import { useQuery } from "@tanstack/react-query";
import { fetchRapidFireBatchList } from "../services/rapidFireService";

const rapidAttemptColors = ["purple", "cyan", "blue", "green"];
const DEFAULT_DAILY_LIMIT = 5;

function parseRemainingAttempts(limitMessage = "") {
  const match = String(limitMessage).match(/(\d+)/);
  return match ? Number(match[1]) : DEFAULT_DAILY_LIMIT;
}

function normalizeBatchItem(item, index) {
  const totalQuestions = Number(item?.total_questions || 0);
  const attemptedQuestions = Number(item?.total_attempted_question || 0);
  const progress = totalQuestions > 0 ? Math.round((attemptedQuestions / totalQuestions) * 100) : 0;

  return {
    id: String(index + 1).padStart(2, "0"),
    batchId: Number(item?.batch_id || 0),
    title: item?.batch_name || `Rapid fire ${index + 1}`,
    date: item?.created_at || "-",
    attempts: attemptedQuestions,
    totalQuestions,
    attemptedQuestions,
    questions: `${attemptedQuestions}/${totalQuestions}`,
    progress,
    color: rapidAttemptColors[index % rapidAttemptColors.length],
    raw: item,
  };
}

export function useRapidFireBatchList(params = {}, options = {}) {
  return useQuery({
    queryKey: ["rapidFireBatchList", params],
    queryFn: async () => {
      const { payload } = await fetchRapidFireBatchList(params);
      const batchList = Array.isArray(payload?.batch_list) ? payload.batch_list : [];
      const leftToday = parseRemainingAttempts(payload?.limit_message || "");
      const totalAttempts = Number(payload?.total_attempts || DEFAULT_DAILY_LIMIT);
      const usedToday = Math.max(0, totalAttempts - leftToday);

      return {
        rapidFireStatus: Number(payload?.rapid_fire_status || 0),
        limitMessage: payload?.limit_message || "",
        stateName: payload?.state_name || "",
        subjectName: payload?.subject_name || "",
        subjectDescription: payload?.subject_description || "",
        categoryName: payload?.category_name || "Rapid Fire",
        usedToday,
        leftToday,
        totalAttempts,
        attempts: batchList.map((item, index) => normalizeBatchItem(item, index)),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        rapidFireStatus: 0,
        limitMessage: "",
        stateName: "",
        subjectName: "",
        subjectDescription: "",
        categoryName: "Rapid Fire",
        usedToday: 0,
        leftToday: DEFAULT_DAILY_LIMIT,
        totalAttempts: DEFAULT_DAILY_LIMIT,
        attempts: [],
      },
  });
}
